/**
 * Banking validation functions
 * IBAN/BIC validation via ibankit, Czech bank account validation via mod11
 * @module banking/validation
 */

import { IBAN, BIC } from 'ibankit';
import { isKnownCzechBankCode } from './czech-bank-codes';

export type BankingValidationErrorCode =
  | 'required'
  | 'invalidFormat'
  | 'unknownBankCode'
  | 'prefixMod11Failed'
  | 'accountMod11Failed'
  | 'ibanRequired'
  | 'ibanTooShort'
  | 'ibanInvalidCountryCode'
  | 'ibanInvalid'
  | 'bicRequired'
  | 'bicInvalidLength'
  | 'bicInvalid'
  | 'schemeUnknown'
  | 'countryCodeInvalid'
  | 'countryMismatch'
  | 'schemeRequiredForCountry'
  | 'bankIdentifierRequired'
  | 'bankIdentifierInvalid'
  | 'accountNumberRequired'
  | 'accountNumberInvalid'
  | 'routingChecksumFailed'
  | 'referenceRequired'
  | 'referenceInvalid'
  | 'referenceTypeUnknown'
  | 'variableSymbolInvalid'
  | 'constantSymbolInvalid'
  | 'specificSymbolInvalid';

export interface BankingValidationError {
  code: BankingValidationErrorCode;
  message: string;
}

export interface BankingValidationResult {
  valid: boolean;
  errors: BankingValidationError[];
}

// ---------------------------------------------------------------------------
// IBAN
// ---------------------------------------------------------------------------

export function isValidIBAN(iban: string): boolean {
  return IBAN.isValid(iban);
}

export function validateIBAN(iban: string): BankingValidationResult {
  const errors: BankingValidationError[] = [];

  if (!iban || iban.trim().length === 0) {
    return { valid: false, errors: [{ code: 'ibanRequired', message: 'IBAN is required' }] };
  }

  const clean = iban.replace(/\s/g, '').toUpperCase();

  if (clean.length < 5) {
    errors.push({ code: 'ibanTooShort', message: 'IBAN is too short' });
  }

  if (!/^[A-Z]{2}/.test(clean)) {
    errors.push({
      code: 'ibanInvalidCountryCode',
      message: 'IBAN must start with a two-letter country code'
    });
  }

  if (!IBAN.isValid(clean)) {
    if (errors.length === 0) {
      errors.push({ code: 'ibanInvalid', message: 'IBAN is invalid' });
    }
  }

  return { valid: errors.length === 0, errors };
}

export interface IBANParts {
  countryCode: string;
  checkDigits: string;
  bankCode: string | null;
  accountNumber: string | null;
}

export function extractIBANParts(iban: string): IBANParts | null {
  const clean = iban.replace(/\s/g, '').toUpperCase();
  if (!IBAN.isValid(clean)) return null;

  const parsed = new IBAN(clean);
  return {
    countryCode: parsed.getCountryCode(),
    checkDigits: parsed.getCheckDigit(),
    bankCode: parsed.getBankCode(),
    accountNumber: parsed.getAccountNumber()
  };
}

// ---------------------------------------------------------------------------
// BIC / SWIFT
// ---------------------------------------------------------------------------

export function isValidBIC(bic: string): boolean {
  return BIC.isValid(bic);
}

export function validateBIC(bic: string): BankingValidationResult {
  const errors: BankingValidationError[] = [];

  if (!bic || bic.trim().length === 0) {
    return { valid: false, errors: [{ code: 'bicRequired', message: 'BIC is required' }] };
  }

  const clean = bic.replace(/\s/g, '').toUpperCase();

  if (clean.length !== 8 && clean.length !== 11) {
    errors.push({ code: 'bicInvalidLength', message: 'BIC must be 8 or 11 characters long' });
  }

  if (!BIC.isValid(clean)) {
    if (errors.length === 0) {
      errors.push({ code: 'bicInvalid', message: 'BIC is invalid' });
    }
  }

  return { valid: errors.length === 0, errors };
}

// ---------------------------------------------------------------------------
// Czech bank account — mod11 validation
// Weights applied right-to-left: [6, 3, 7, 9, 10, 5, 8, 4, 2, 1]
// (i.e. rightmost digit × 1, next × 2, … leftmost × 6)
// The weighted sum must be divisible by 11.
// ---------------------------------------------------------------------------

const MOD11_WEIGHTS = [6, 3, 7, 9, 10, 5, 8, 4, 2, 1] as const;

export function mod11(numberString: string): boolean {
  if (!/^\d+$/.test(numberString)) return false;

  const padded = numberString.padStart(10, '0');
  if (padded.length > 10) return false;

  let sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(padded[i], 10) * MOD11_WEIGHTS[i];
  }

  return sum % 11 === 0;
}

// ---------------------------------------------------------------------------
// Czech bank account parsing & validation
// Format: [prefix-]accountNumber/bankCode
// Prefix: 0–6 digits, accountNumber: 2–10 digits, bankCode: 4 digits
// ---------------------------------------------------------------------------

export interface CzechBankAccountParts {
  prefix?: string;
  accountNumber: string;
  bankCode: string;
}

const CZ_ACCOUNT_REGEX = /^(?:(\d{1,6})-)?(\d{2,10})\/(\d{4})$/;

export function parseCzechBankAccount(account: string): CzechBankAccountParts | null {
  const match = account.trim().match(CZ_ACCOUNT_REGEX);
  if (!match) return null;

  return {
    prefix: match[1] || undefined,
    accountNumber: match[2],
    bankCode: match[3]
  };
}

export function validateCzechBankAccount(account: string): BankingValidationResult {
  const errors: BankingValidationError[] = [];

  if (!account || account.trim().length === 0) {
    return { valid: false, errors: [{ code: 'required', message: 'Bank account is required' }] };
  }

  const parts = parseCzechBankAccount(account);
  if (!parts) {
    return {
      valid: false,
      errors: [
        {
          code: 'invalidFormat',
          message: 'Invalid Czech bank account format (expected [prefix-]accountNumber/bankCode)'
        }
      ]
    };
  }

  if (!isKnownCzechBankCode(parts.bankCode)) {
    errors.push({ code: 'unknownBankCode', message: `Unknown bank code: ${parts.bankCode}` });
  }

  if (parts.prefix && !mod11(parts.prefix)) {
    errors.push({ code: 'prefixMod11Failed', message: 'Account prefix fails mod11 check' });
  }

  if (!mod11(parts.accountNumber)) {
    errors.push({ code: 'accountMod11Failed', message: 'Account number fails mod11 check' });
  }

  return { valid: errors.length === 0, errors };
}

export function isValidCzechBankAccount(account: string): boolean {
  return validateCzechBankAccount(account).valid;
}

// ---------------------------------------------------------------------------
// Czech payment symbols — numeric only
// ---------------------------------------------------------------------------

/** Max digit lengths for the Czech payment symbols. */
export const VARIABLE_SYMBOL_MAX_LENGTH = 10;
export const CONSTANT_SYMBOL_MAX_LENGTH = 4;
export const SPECIFIC_SYMBOL_MAX_LENGTH = 10;

const VARIABLE_SYMBOL_REGEX = new RegExp(`^\\d{1,${VARIABLE_SYMBOL_MAX_LENGTH}}$`);
const CONSTANT_SYMBOL_REGEX = new RegExp(`^\\d{1,${CONSTANT_SYMBOL_MAX_LENGTH}}$`);
const SPECIFIC_SYMBOL_REGEX = new RegExp(`^\\d{1,${SPECIFIC_SYMBOL_MAX_LENGTH}}$`);

export function isValidVariableSymbol(symbol: string): boolean {
  return VARIABLE_SYMBOL_REGEX.test(symbol.trim());
}

export function isValidConstantSymbol(symbol: string): boolean {
  return CONSTANT_SYMBOL_REGEX.test(symbol.trim());
}

export function isValidSpecificSymbol(symbol: string): boolean {
  return SPECIFIC_SYMBOL_REGEX.test(symbol.trim());
}
